import apiClient from './api';

export type SignupLocationKind = 'provinces' | 'municipalities' | 'barangays';

export type SignupLocationRow = {
  id: string | number;
  code?: string | null;
  name: string;
  province_id?: string | null;
  city_id?: string | null;
};

type Paginated = {
  success?: boolean;
  data?: {
    data?: SignupLocationRow[];
    current_page?: number;
    last_page?: number;
    total?: number;
  };
  message?: string;
};

export async function searchSignupLocations(
  kind: SignupLocationKind,
  opts?: {
    search?: string;
    page?: number;
    per_page?: number;
    province_id?: string;
    city_id?: string;
  }
): Promise<SignupLocationRow[]> {
  const params: Record<string, string | number> = {
    page: opts?.page ?? 1,
    per_page: opts?.per_page ?? 20,
  };
  if (opts?.search?.trim()) params.search = opts.search.trim();
  if (opts?.province_id) params.province_id = opts.province_id;
  if (opts?.city_id) params.city_id = opts.city_id;

  const { data } = await apiClient.get<Paginated>(`/webgis/locations/${kind}`, {
    params,
    // Public signup lookups — never trigger /auth/refresh on failure.
    _skipAuthRefresh: true,
  } as Parameters<typeof apiClient.get>[1]);
  const rows = data?.data?.data;
  return Array.isArray(rows) ? rows : [];
}

function namesMatch(left: unknown, right: unknown): boolean {
  const a = String(left ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  const b = String(right ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  if (!a || !b) return false;
  return a === b || a.includes(b) || b.includes(a);
}

/** Digisaka ``philippine_barangays.id`` is a short integer, not a 9-digit PSGC. */
export function isDigisakaBarangayRowId(value: unknown): boolean {
  const raw = String(value ?? '').trim();
  return /^\d{1,7}$/.test(raw);
}

/**
 * LGU farm create on digisaka.app looks up ``philippine_barangays.id``.
 * GEE/PSGC barangay codes fail that check — resolve the row by name + city.
 */
export async function resolveDigisakaBarangay(
  barangayName: string,
  cityId?: string | null
): Promise<SignupLocationRow | null> {
  const search = String(barangayName || '').trim();
  if (!search) return null;

  const cityCandidates = [String(cityId || '').trim()].filter(Boolean);
  const cityDigits = String(cityId || '').replace(/\D/g, '');
  if (cityDigits.length >= 4 && cityDigits[2] === '0') {
    const stripped = cityDigits.slice(0, 2) + cityDigits.slice(3);
    if (stripped && !cityCandidates.includes(stripped)) cityCandidates.push(stripped);
  }
  if (cityDigits && !cityCandidates.includes(cityDigits)) cityCandidates.push(cityDigits);

  for (const city_id of [...cityCandidates, undefined]) {
    try {
      const rows = await searchSignupLocations('barangays', {
        search,
        city_id,
        per_page: 50,
      });
      const exact = rows.find((r) => namesMatch(r.name, search));
      if (exact) return exact;
      if (rows.length === 1) return rows[0];
    } catch {
      /* try next city filter */
    }
  }
  return null;
}
